import { PermissionChartTemplate } from "app/storybookComponents/Charts/types";

export type CoachBoWeeklyCheckInActivityReports = Record<
  string,
  CoachBoWeeklyCheckInActivityReport
>;

export interface ManagerCustomListItem {
  userAccountId: number;
  entireReportingStructure?: boolean;
}

export interface CoachBoWeeklyCheckInActivityReport {
  completionInfo: {
    percentage: number;
    completedCount: number | null;
    totalCount: number | null;
    trendDirection: string | null;
    trendAmount: number | null;
  };
  energy: {
    score: number | null;
    trendDirection: string | null;
    trendAmount: number | null;
  };
  progress: {
    score: number | null;
    trendDirection: string | null;
    trendAmount: number | null;
  };
  readiness: {
    score: number | null;
    trendDirection: string | null;
    trendAmount: number | null;
  };
  // AI-Service (DBC-661) adds this field; Laravel does not return it.
  scoresHiddenForAnonymity?: boolean;
}

export interface HrConcernAlert {
  id: number;
  severity: "low" | "medium" | "high" | "critical";
  categories: string[];
  excerpt: string;
  detectedAt: string;
}

export interface WeeklyCheckInTableRowDataBase {
  moodScore: number | null;
  progressScore: number | null;
  readinessScore: number | null;
}

export interface UserWeeklyCheckInTableRowData
  extends WeeklyCheckInTableRowDataBase {
  threadId: string;
  completedDate: string;
  userAccountId: number;
  errorGeneratingScores: boolean | 0 | 1;
  gradingCount?: number;
  isSummaryHidden?: boolean;
  winSpotlight?: WinSpotlightType | null;
  issueSpotlight?: IssueSpotlightType | null;
  isTurnoverRisk: BooleanSetting;
  isBurnoutRisk: BooleanSetting;
  conversationStarters?: string[] | null;
  primaryTheme?: string | null;
  wasConversationGraded?: boolean;
  hrConcern?: HrConcernAlert | null;
}

export interface GroupWeeklyCheckInTableRowData
  extends WeeklyCheckInTableRowDataBase {
  entityId: number;
  completedCount: number;
  invitedCount: number;
  weeklyCheckInInstanceId: number;
  created_at: string;
  startDate: string;
  endDate: string;
  threadIds: string[];
  burnoutRiskCount?: number;
  turnoverRiskCount?: number;
  department: string | null;
}

export type WeeklyCheckInTableRowData =
  | UserWeeklyCheckInTableRowData
  | GroupWeeklyCheckInTableRowData;

export type WeeklyCheckInTableDataByEntityType = {
  [entityType: string]: WeeklyCheckInTableRowData[] | undefined;
};

export type AllWeeklyCheckInTableData = {
  [timeInterval: string]: WeeklyCheckInTableDataByEntityType | undefined;
};

export const isWeeklyCheckInRowUser = (
  row: WeeklyCheckInTableRowData
): row is UserWeeklyCheckInTableRowData => {
  return (row as UserWeeklyCheckInTableRowData).threadId !== undefined;
};

export interface CheckInResponseSummaryResponse {
  primaryTheme?: string | null;
  secondaryTheme?: string | null;
  conversationStarters?: string[] | null;
  issueSpotlight?: IssueSpotlightType | null;
  winSpotlight?: WinSpotlightType | null;
  emailReports?: WeeklyCheckInActivityEmailReportTableData["reports"];
}

export interface WeeklyCheckInLineChartResponse {
  labels: string[];
  averageEnergy: (number | null)[];
  averageProgress: (number | null)[];
  averageReadiness: (number | null)[];
  counts: number[];
  hasPreviousTimePeriodData?: boolean;
  hasNextTimePeriodData?: boolean;
}

export interface WeeklyCheckInLineChartState {
  [timeInterval: string]: {
    [index: number]: WeeklyCheckInLineChartResponse | null;
  };
}

export type SelectedCheckInBreakdownEntity = {
  entityType: "team" | "department";
  entityId: number;
  weeklyCheckInInstanceId: number;
  startDate: string;
  endDate: string;
};

export interface WeeklyCheckInUserStatuses {
  pendingUsers: number[];
  usersWhoHaveBeenRemindedInTheLastFiveMinutes: number[];
}

export type BooleanSetting = 0 | 1 | boolean | null | undefined;

export interface WeeklyCheckInRegradeData extends WeeklyCheckInTableRowDataBase {
  isBurnoutRisk?: BooleanSetting;
  isTurnoverRisk?: BooleanSetting;
}

// TODO (Feature clean up):
//  1. We should replace the WeeklyCheckInCompanySettings type to CheckInCompanySettings
//  2. We should also update the backend to strip the word "Weekly" from the response keys
//  3. We should also update the thunk from getWeeklyCheckInCompanySettings to like getCheckInCompanySettings
//  4. We should also update the actual endpoint from GET /weeklyCheckInsCompanySettings to GET /checkInsCompanySettings
export interface WeeklyCheckInCompanySettings {
  hasWeeklyCheckInsEnabledForEntireCompany: BooleanSetting;
  hasWeeklyCheckInSeniorManagerReportEnabled: BooleanSetting;
  hasWeeklyCheckInTeamLeaderReportEnabled: BooleanSetting;
  hasWeeklyCheckInExecutiveReportEnabledForAllAdmins: BooleanSetting;
  hasWeeklyCheckInsEnabled: BooleanSetting;
  hasWeeklyCheckInExecutiveReportEnabledForCustomList: BooleanSetting;
  visibilityDefault?: VisibilityDefaultType;
  reportVisibilitySettings?:
    | "individual-and-admin"
    | "everyone-with-analyses-access";
  hasMerchandisingEnabledForAdmins: BooleanSetting;
  hasMerchandisingEnabledForTeamLeaders: BooleanSetting;
  hasMerchandisingEnabledForManagers: BooleanSetting;
  visibilityMode?: "open" | "conservative";
  checkInFrequency?: WeeklyCheckInCadence;
  inProgressCount?: number;
  isHarmfulDetectionEnabledForCheckIns: BooleanSetting;
  nonAdminsCanEditCheckInRecipients: BooleanSetting;
  harmDetectionNotificationMode?:
    | "all_with_access"
    | "admins_only"
    | "specific_admins";
}

export interface WeeklyCheckInCustomList {
  managers: ManagerCustomListItem[];
  departments: number[];
  teams: number[];
}
export type VisibilityDefaultType =
  | "privacy-focused"
  | "team-focused"
  | "development-focused";

export interface WeeklyCheckInVisibilityDefault {
  responseSummary: PermissionChartTemplate;
  checkInScores: PermissionChartTemplate;
}
// ─── Shared Report Types ───
export type ReportMode = "all_who_qualify" | "specific_people";

export type ReportRecipientStatus =
  | "qualified_by_rules"
  | "included_override"
  | "excluded"
  | "below_threshold"
  | "not_included";

export interface ReportUser {
  userAccountId: number;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  overrideCreatedByName?: string | null;
  overrideCreatedAt?: string | null;
}

export interface ReportSummary {
  qualifiedByRules: number;
  includedOverride: number;
  excluded: number;
  belowThreshold: number;
  notIncluded: number;
}

export interface BaseRecipient {
  userAccountId: number;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  percentInvited: number;
  status: ReportRecipientStatus;
  reason: string;
  overrideCreatedByName: string | null;
  overrideCreatedAt: string | null;
  alsoQualifies: boolean;
}

export interface SeniorManagerReportPreviewRecipient extends BaseRecipient {
  spanSize: number;
}
export interface TeamLeaderReportPreviewRecipient extends BaseRecipient {
  teamCount: number;
  teamMemberCount: number;
}

export interface ReportPreview<T extends BaseRecipient> {
  recipients: T[];
  summary: ReportSummary;
}

// Structural base used by the generic ReportPreviewDrawer; both
// SeniorManagerReportSettings and TeamLeaderReportSettings satisfy this shape.
export interface ReportSettings {
  mode: ReportMode | null;
  thresholdPercentage: number | null;
  excludedUsers: { userAccountId: number }[];
  includedUsers: { userAccountId: number }[];
}

export interface UpdatePayload {
  mode: string;
  thresholdPercentage: number | null;
  excludedUserAccountIds: number[];
  includedUserAccountIds: number[];
}

// ─── Senior Manager Report Settings ───

export interface SeniorManagerReportSettings {
  mode: ReportMode | null;
  thresholdPercentage: number | null;
  excludedUsers: ReportUser[];
  includedUsers: ReportUser[];
}

export type SeniorManagerReportPreview =
  ReportPreview<SeniorManagerReportPreviewRecipient>;

// ─── Team Leader Report Settings ───

export interface TeamLeaderReportSettings {
  mode: ReportMode | null;
  thresholdPercentage: number | null;
  excludedUsers: ReportUser[];
  includedUsers: ReportUser[];
}

export type TeamLeaderReportPreview =
  ReportPreview<TeamLeaderReportPreviewRecipient>;

export interface WeeklyCheckInActivityEmailReportTableData {
  userAccountId: number;
  dateSent: string;
  reports: {
    reportType: "teamLeader" | "seniorManager";
    checkInEmailReportId: number;
    teamId?: number | null;
  }[];
}

export interface WeeklyCheckInActivityEmailReport {
  averageMoodScore: number;
  averageProgressScore: number;
  averageReadinessScore: number;
  rawHtmlReportText: string;
  reportType: "teamLeader" | "seniorManager";
  teamId?: number;
}

export type WeeklyCheckInActivityEmailReportState = {
  [checkInEmailReportId: number]: WeeklyCheckInActivityEmailReport;
};

export type WeeklyCheckinEmailReportDataByTimeAndType = {
  [timeInterval: string]:
    | {
        [entityType: string]:
          | WeeklyCheckInActivityEmailReportTableData[]
          | undefined;
      }
    | undefined;
};

export interface SampleWeeklyCheckInData {
  checkInActivity: CoachBoWeeklyCheckInActivityReports;
  weeklyCheckInLineChartData: WeeklyCheckInLineChartState;
  weeklyCheckInTableData: AllWeeklyCheckInTableData;
  analyses: Record<string, CheckInResponseSummaryResponse>;
  emailReportTableData?: WeeklyCheckInActivityEmailReportTableData[];
  checkInEmailReportMap?: Record<number, WeeklyCheckInActivityEmailReport>;
  celebrateAndSupport?: CelebrateAndSupports;
}

export type CheckInEmailReportType =
  | "check-in-email"
  | "team-leader-report"
  | "senior-manager-report"
  | "executive-report"
  | "employee-conversation-summary-report";

export interface SampleWeeklyCheckInEmailReportModalState {
  isOpen?: boolean;
  type?: CheckInEmailReportType | "sample-weekly-check-in-chat";
}

export type IssueSpotlightType = {
  action?: string | null;
  impact?: string | null;
  issue?: string | null;
  severity?: number;
};

export type WinSpotlightType = {
  impact?: string | null;
  magnitude?: number;
  achievement?: string | null;
  recognition?: string | null;
};

export type CelebrateOrSupportType = {
  userAccountId: number;
  threadId?: string;
  isBurnoutRisk: BooleanSetting;
  isTurnoverRisk: BooleanSetting;
  issueSpotlight?: IssueSpotlightType | null;
  winSpotlight?: WinSpotlightType | null;
  updated_at?: string;
};

export interface CelebrateAndSupports {
  celebrate: CelebrateOrSupportType[];
  support: CelebrateOrSupportType[];
}

export interface GetCelebrateAndSupportUsersPayload {
  timeFrame: "7Days" | "30Days" | "365Days";
  teamId?: number;
  departmentId?: number;
}

export type CheckInPattern =
  | "No Issues"
  | "Things Improving"
  | "Recent Challenge"
  | "May Need Support";

/** Common fields shared by both the Laravel and AI-Service snapshot responses. */
interface ICSnapshotDataBase {
  issuesRaised: string[]; // Any issues raised in the last 4 check-ins
  wins: string[]; // Any wins raised recently
  streak: number;
  hasCompletedPreviousCheckIn: boolean;
  pattern?: CheckInPattern;
}

/** Shape returned by the Laravel endpoint for companies NOT yet on the AI-Service migration. */
export interface ICSnapshotData extends ICSnapshotDataBase {
  overallScores: { overAllScore?: number }[]; // scores will vary from 1-5
}

/**
 * Shape returned by the AI-Service GET /individualCheckInActivitySnapshot endpoint
 */
export interface AiServiceICSnapshotData extends ICSnapshotDataBase {
  overallScores: { periodStart: string; overAllScore: number | null }[];
}

export interface ExternalReportRecipient {
  id: number;
  emailAddress: string;
  recipientName: string | null;
}

export type WeeklyCheckInCadence = "weekly" | "biweekly" | "monthly";

export interface WeeklyCheckInCadencePayload {
  checkInFrequency: WeeklyCheckInCadence;
}

// Shape of GET /v1/weeklyCheckInCadence/schedule (AI-Service, DBC-704).
export interface WeeklyCheckInCadenceScheduleCurrentCycle {
  windowOpensUtc: string | null;
  windowClosesUtc: string | null;
  isWindowOpen: boolean;
  reportSendUtc: string | null;
}

// Only present when the request includes `previewFrequency`.
export interface WeeklyCheckInCadenceSchedulePreview {
  wouldBeBlocked: boolean;
  canApplyAtUtc: string;
  nextCycleStartUtc: string;
  firstEmailUtc: string;
}

export interface WeeklyCheckInCadenceSchedule {
  currentFrequency: WeeklyCheckInCadence;
  currentCycle: WeeklyCheckInCadenceScheduleCurrentCycle;
  nextCycleStartUtc: string;
  preview?: WeeklyCheckInCadenceSchedulePreview;
}
